/**
 * Locating the pipeline's proposed spans inside a caption.
 *
 * The orchestration output reports a span as quoted TEXT, not as character offsets, and
 * that text does not always appear verbatim: it may be one of several alternatives
 * joined by " / ", it may be prefixed "from span:", and whitespace may differ. The old
 * tool had a chain of fuzzy locators for this; the same idea is implemented here, with
 * the match quality reported so an approximate hit can be shown as such rather than
 * silently presented as exact.
 */

import { phraseVariants } from './agentCaption.js';

/** Case-insensitive literal search, preferring the occurrence nearest `near`. */
function findLiteral(haystack, needle, near = 0) {
  if (!needle) return null;
  const hay = haystack.toLowerCase();
  const pin = needle.toLowerCase();
  const hits = [];
  for (let i = hay.indexOf(pin); i >= 0; i = hay.indexOf(pin, i + 1)) {
    hits.push({ start: i, end: i + needle.length });
  }
  if (!hits.length) return null;
  return hits.sort((a, b) => Math.abs(a.start - near) - Math.abs(b.start - near))[0];
}

/**
 * Whitespace-tolerant search: matches the needle's words with any run of whitespace
 * between them, so a span broken across a line break still resolves.
 */
function findLoose(haystack, needle) {
  const words = String(needle || '').trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return null;
  const pattern = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('\\s+');
  const match = new RegExp(pattern, 'i').exec(haystack);
  return match ? { start: match.index, end: match.index + match[0].length } : null;
}

/**
 * Locate one pipeline span.
 * @returns {{start:number,end:number,exact:boolean,matched:string}|null}
 */
export function locateSpan(caption, spanText, near = 0) {
  const text = String(caption || '');
  for (const variant of phraseVariants(spanText)) {
    const exact = findLiteral(text, variant, near);
    if (exact) return { ...exact, exact: true, matched: variant };
  }
  for (const variant of phraseVariants(spanText)) {
    const loose = findLoose(text, variant);
    if (loose) return { ...loose, exact: false, matched: variant };
  }
  return null;
}

/**
 * Build the decision queue: the pipeline's spans followed by its missing details,
 * ordered by where each one sits in the caption so the admin works top to bottom
 * rather than in the order the pipeline happened to emit them.
 */
export function buildQueue(finalSpans = [], missingDetails = [], caption = '') {
  const items = [];

  finalSpans.forEach((span, index) => {
    const hit = locateSpan(caption, span.span_text || span.claim_or_span);
    items.push({
      kind: 'span',
      index,
      id: `span:${index}`,
      text: span.span_text || span.claim_or_span || '',
      label: span.final_label || '',
      action: span.final_action || '',
      replacement: span.replacement || '',
      uncertaintyNote: span.uncertainty_note || '',
      reason: span.reason || '',
      position: hit ? hit.start : Number.MAX_SAFE_INTEGER,
      range: hit,
      source: span,
    });
  });

  missingDetails.forEach((detail, index) => {
    const text = typeof detail === 'string' ? detail : detail?.detail_text || detail?.detail || '';
    items.push({
      kind: 'missing',
      index,
      id: `missing:${index}`,
      text,
      label: 'missing_detail',
      action: 'add',
      reason: '',
      // A verified missing detail is one the run ADDED, so its wording is often present
      // in the edited caption and can be highlighted there - that is what the old tool
      // showed in cyan. `position` stays at the end regardless: a detail is a judgement
      // about what was absent, so it belongs after the span decisions in the queue even
      // when its added text happens to sit early in the caption.
      position: Number.MAX_SAFE_INTEGER,
      range: locateSpan(caption, text),
      source: detail,
    });
  });

  return items
    .map((item, order) => ({ ...item, order }))
    .sort((a, b) => a.position - b.position || a.order - b.order);
}

const DECISION_CLASS = {
  confirmed: 'agent-confirmed',
  rejected: 'agent-rejected',
  wrong_label_or_action: 'agent-wrong_label_or_action',
};

//: Missing details keep their own colour family, as the old tool had them: a detail is
//: text the run ADDED, not a fault it found, so it must not read as a span verdict.
const DETAIL_CLASS = {
  confirmed: 'agent-detail-confirmed',
  rejected: 'agent-detail-rejected',
};

/**
 * Highlight ranges for the agent's spans and for any verified missing detail whose text
 * can be found in the caption, coloured by the admin's verdict. `activeId` gets the
 * cursor outline.
 *
 * Span verdicts and detail verdicts are indexed separately (span_index vs
 * detail_index), so both maps are needed - passing one for both would colour a detail
 * by whatever the span at the same ordinal happened to be.
 */
export function agentRanges(queue, decisionsByIndex, activeId, detailDecisionsByIndex = {}) {
  return queue
    .filter((item) => item.range)
    .map((item) => {
      const missing = item.kind === 'missing';
      const decision = missing
        ? detailDecisionsByIndex[item.index]?.decision
        : decisionsByIndex[item.index]?.decision;
      const classes = missing
        ? ['agent-detail', DETAIL_CLASS[decision] || 'agent-detail-pending']
        : [DECISION_CLASS[decision] || 'agent-pending'];
      if (!item.range.exact) classes.push('agent-approximate');
      return {
        start: item.range.start,
        end: item.range.end,
        className: classes.join(' '),
        title: missing
          ? `added missing detail${decision ? ` · ${decision}` : ' · pending'}${
              item.range.exact ? '' : ' (approximate match)'
            }`
          : `${item.label} · ${item.action}${decision ? ` · ${decision}` : ' · pending'}${
              item.range.exact ? '' : ' (approximate match)'
            }`,
        active: item.id === activeId,
      };
    });
}
